import {useEffect} from 'react';
import {ValueValidator} from '../Validator';
import {RootState} from "../../../../app/store";
import {useAppSelector} from "../../../../app/hooks";

export type ValidationFieldName<T> = keyof T | string;
export type InvalidStatus<T> = { valid: false, fieldName: ValidationFieldName<T>, dirty: boolean, message: string | undefined };
export type ValidStatus<T> = { valid: true, fieldName: ValidationFieldName<T>, dirty: boolean }
export type ValidationResult<T> = InvalidStatus<T> | ValidStatus<T>;

export type OnValidChangeHandler<T> = (result: ValidationResult<T>) => void;

interface Props<PROPERTY, OBJECT> {
    selector: (state: RootState) => PROPERTY | undefined;
    required?: boolean;
    fieldName: ValidationFieldName<OBJECT>;
    onChange: (value: PROPERTY) => any,
    updateValidationStatus: OnValidChangeHandler<OBJECT>,
    validator?: ValueValidator<PROPERTY>;
    disabled?: boolean;
    label: string;
    child: (
        label: string,
        value: PROPERTY | undefined,
        onChange: (value: PROPERTY) => any,
        required: boolean
    ) => JSX.Element | JSX.Element[];
}

export const ValidationComponent = <PROPERTY, OBJECT>({
                                                          selector,
                                                          required = true,
                                                          fieldName,
                                                          label,
                                                          onChange,
                                                          updateValidationStatus,
                                                          validator,
                                                          disabled,
                                                          child
                                                      }: Props<PROPERTY, OBJECT>) => {

    const value = useAppSelector(selector);

    useEffect(() => {
        if (validator && !disabled) {
            const maybePromise = validator(label, value);

            if (maybePromise instanceof Promise) {
                maybePromise.then(outcome =>
                    onValidationStatusChange({...outcome, dirty: !!value})
                ).catch(() =>
                    onValidationStatusChange({valid: false, dirty: !!value, message: "Validation failed"})
                );
            } else {
                onValidationStatusChange({...maybePromise, dirty: !!value});
            }
        } else {
            onValidationStatusChange({dirty: false, valid: true});
        }

        return () => {
            onValidationStatusChange({valid: true, dirty: false});
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, disabled]);

    function onValidationStatusChange(validationOutcome: { valid: false; message?: string; dirty: boolean } | {
        valid: true,
        dirty: boolean
    }) {
        if (!validationOutcome.valid) {
            const {valid, message} = validationOutcome;
            updateValidationStatus({valid, fieldName, dirty: validationOutcome.dirty, message});
        } else {
            updateValidationStatus({valid: true, fieldName, dirty: validationOutcome.dirty});
        }
    }

    const handleChange = (value: PROPERTY) => {
        onChange(value);
    }

    return <>
        {child(label, value, handleChange, required)}
    </>;
};
